import queryString from 'query-string';
import { StorageWrapper } from './Storage/StorageWrapper';

const DEV = /^(stg|dev|demo|sandbox|qa)/;
const DEV_HOST = /.*(firebaseapp\.com|ngrok\.io|netlify\.app)/;
const NETLIFY_HOST = /.*(netlify\.app)/;
const LOCALHOST = /^(localhost|192|10|local)$/;
const DEV_ENV = /(dev.|demo.)+\.dev\.clover\.com$/;
const STAGING_ENV = /stg\d+\.dev\.clover\.com|staging\.clover\.com/;
const PROD_ENV = /^(www\.)?((eu|la)\.)?clover\.com$/;
const CLOVER_HOST = /^(.*\.)?clover\.com$/;
const wwwRegex = /\bwww\b/i; // Case-insensitive regex for 'www'
const HTTPS = 'https://';

export type Environment = 'test' | 'development' |'production';

/**
 * Is a Dev based location (stg1, dev1, firebase, etc)
 */
export const isDevEnvironment = (hostname: string = getHostname()) => {
  const isDev = DEV.test(hostname.split('.')[0]);

  return isLocalhost(hostname) || isDev || isDevHost(hostname);
};

/**
 * Is Localhost based on hostname
 */
export const isLocalhost = (hostname: string = getHostname()) => LOCALHOST.test(hostname.split('.')[0]);

/**
 * Is a non-Clover dev host (firebase, netlify, etc)
 */
export const isDevHost = (hostname: string = getHostname()) => DEV_HOST.test(hostname);

/**
 * Is a Clover dev environment (devX.dev.clover.com)
 */
export const isCloverDevEnvironment = (hostname: string = getHostname()) => DEV_ENV.test(hostname);

/**
 * Is a Clover stage environment (stgX.dev.clover.com)
 */
export const isCloverStagingEnvironment = (hostname: string = getHostname()) => STAGING_ENV.test(hostname);

/**
 * Is a Clover prod environment (www.clover.com)
 */
export const isCloverProdEnvironment = (hostname: string = getHostname()) => isCloverHost(hostname) && PROD_ENV.test(hostname);

/**
 * Is a Cypress Environment
 */
export const isCypressEnvironment = () => !!window.Cypress;

/**
 * Is a Netlify host
 */
export const isNetlifyHost = (hostname: string = getHostname()) => NETLIFY_HOST.test(hostname);

export const isCloverHost = (hostname: string = getHostname()) => CLOVER_HOST.test(hostname);

export const getHostname = () => window.location.hostname;

export const getSubdomain = () => getHostname().includes('www') ? getHostname().split('.')[1] :getHostname().split('.')[0] ;

export const getCloverEnvironmentName = (hostname: string = getHostname()) => {

 const envHostname = hostname.split('.')[0];
 const isWwwPresent = wwwRegex.test(envHostname);
 const hostnameOrigin = isWwwPresent ? `https://${hostname.split('.')[1]}` : envHostname;

 return hostnameOrigin.replace(/[0-9]/g, '');

};

/**
 * To use a different env on Netlify add queryparam of env
 * ex. ?env=stg3
 */
export const getCloverApiHostnameForQueryParam = (hostname: string = getHostname()) => {
  const storage = StorageWrapper.create(() => window.sessionStorage);
  const { env } = queryString.parse(window.location.search) || storage.tryGetItem('env');

  if (env && typeof env === 'string') {
    storage.trySetItem('env', env);
    const isDev = DEV.test(env);

    if (isDev) hostname = `${env}.dev.clover.com`;
  } else {
    // set default state for netlify
    hostname = 'dev1.dev.clover.com';
  }

  return [`${window.location.protocol}//${hostname}`, window.location.port].filter(s => !!s).join(':');
};

export const getCloverApiHostname = (hostname: string = getHostname()) => {
  if (isNetlifyHost() && !isCypressEnvironment()) return getCloverApiHostnameForQueryParam();
  // Handle local.dev.clover.com
  if (isCloverHost(hostname) && !isLocalhost(hostname)) return `${HTTPS}${hostname}`;

  return [`${window.location.protocol}//${hostname}`, window.location.port].filter(s => !!s).join(':');
};

const ceTargetMap: Record<string, string> = {
  local: 'https://apidev1.dev.clover.com/customer-engagement',
  dev: 'https://apidev1.dev.clover.com/customer-engagement',
  stg: 'https://apistg1.dev.clover.com/customer-engagement',
  sandbox: 'https://apistg1.dev.clover.com/customer-engagement',
  prod: 'https://api.clover.com/customer-engagement',
  staging: 'https://api.staging.clover.com/customer-engagement'
};

export const getCeApiHostname = () => {
  if (isLocalhost()) return ceTargetMap['local'];

  return ceTargetMap[getCloverEnvironmentName(getCloverApiHostname()).split(HTTPS)[1]] ?? ceTargetMap['prod'];
};

export const getTokenizationEnv = (hostname: string = getHostname()) => {
  if (isCloverProdEnvironment(hostname)) return 'token';
  const envHostname = hostname.split('.')[0];
  const isWwwPresent = wwwRegex.test(envHostname);
  const hostnameOrigin = isWwwPresent ? `${hostname.split('.')[1]}` : envHostname;
  const isincludesDev = hostname.includes('dev');

  return isincludesDev ? `token-${hostnameOrigin.split('.')[0]}.dev` :`token-${hostnameOrigin.split('.')[0]}`;
};
